import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import PageHero from "../components/PageHero";
import SocialEmbed from "../components/SocialEmbed";
import TestimonialsStrip from "../components/TestimonialsStrip";
import SEO from "../components/SEO";
import { ERFOLGE_CATEGORIES, ERFOLGE_ITEMS } from "../data/mock";
import { fetchErfolge, resolveAssetUrl } from "../lib/api";

// Video card with hover-autoplay
const VideoCard = ({ item, onOpen }) => {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);

  const handleEnter = () => {
    setHover(true);
    if (ref.current) {
      ref.current.currentTime = 0;
      const p = ref.current.play();
      if (p && p.catch) p.catch(() => {});
    }
  };
  const handleLeave = () => {
    setHover(false);
    if (ref.current) {
      ref.current.pause();
      ref.current.currentTime = 0;
    }
  };

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={() => onOpen(item)}
      className="group relative aspect-[4/5] rounded-3xl overflow-hidden cursor-pointer bg-neutral-900"
    >
      <video
        ref={ref}
        src={item.src + "#t=0.5"}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

      {/* Play button */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/95 backdrop-blur flex items-center justify-center transition-all duration-500 ${
          hover
            ? "opacity-0 scale-90"
            : "opacity-100 scale-100 shadow-[0_0_40px_rgba(255,255,255,0.4)]"
        }`}
      >
        <Icons.Play size={22} className="text-neutral-950 fill-neutral-950 ml-1" />
      </div>

      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-xs font-semibold flex items-center gap-1.5">
        <Icons.Video size={12} />
        Video
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-bold">
          {item.subtitle}
        </div>
        <h3 className="mt-1 font-display font-extrabold text-white text-xl md:text-2xl tracking-tight">
          {item.title}
        </h3>
        {item.metric && (
          <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 text-white text-xs font-bold">
            {item.metric}
          </div>
        )}
      </div>
    </div>
  );
};

const ImageCard = ({ item, onOpen }) => (
  <div
    onClick={() => onOpen && onOpen(item)}
    className="group relative aspect-[4/5] rounded-3xl overflow-hidden cursor-pointer"
    style={{ background: item.gradient }}
  >
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
    <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
      <Icons.ArrowUpRight size={16} className="text-white" />
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-bold">
        {item.subtitle}
      </div>
      <h3 className="mt-1 font-display font-extrabold text-white text-xl md:text-2xl tracking-tight">
        {item.title}
      </h3>
      {item.metric && (
        <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 text-white text-xs font-bold">
          {item.metric}
        </div>
      )}
    </div>
  </div>
);

// Brand card – shows actual client logo on its native background, label at bottom
const BrandCard = ({ item, onOpen }) => {
  const isDark = item.logo?.bg === "dark";
  return (
    <div
      onClick={() => onOpen && onOpen(item)}
      className="group relative aspect-[4/5] rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)]"
    >
      {/* Logo area – top 65% */}
      <div
        className={`absolute inset-x-0 top-0 h-[65%] flex items-center justify-center px-8 ${
          isDark ? "bg-neutral-900" : "bg-white"
        }`}
      >
        <img
          src={item.logo.src}
          alt={item.title}
          className="max-w-[78%] max-h-[78%] object-contain transition-transform duration-500 group-hover:scale-[1.06]"
          loading="lazy"
        />
      </div>
      {/* Label area – bottom 35% */}
      <div className="absolute inset-x-0 bottom-0 h-[35%] bg-neutral-950 text-white p-5 md:p-6 flex flex-col justify-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 font-bold">
          {item.subtitle}
        </div>
        <h3 className="mt-1 font-display font-extrabold text-xl md:text-2xl tracking-tight">
          {item.title}
        </h3>
      </div>
      {/* Decorative gradient strip at the divider */}
      <div className="absolute left-0 right-0 top-[65%] h-[2px] bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500" />
    </div>
  );
};

// Photo gallery card (for "Fotos" category)
const PhotoCard = ({ item, span, onOpen }) => {
  if (item.type === "photo") {
    return (
      <div
        onClick={() => onOpen && onOpen(item)}
        className={`group relative rounded-2xl overflow-hidden cursor-pointer ${
          span ? "row-span-2 aspect-[3/5]" : "aspect-square"
        }`}
      >
        <img
          src={item.src}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 ring-1 ring-white/10 group-hover:ring-2 group-hover:ring-pink-500/40 rounded-2xl transition-all duration-500 group-hover:shadow-[0_0_60px_rgba(236,72,153,0.4)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
          <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 font-bold">
            {item.subtitle}
          </div>
          <div className="font-display font-extrabold text-white text-lg tracking-tight mt-0.5">
            {item.title}
          </div>
        </div>
      </div>
    );
  }
  // gradient placeholder
  return (
    <div
      className={`group relative rounded-2xl overflow-hidden cursor-pointer ${
        span ? "row-span-2 aspect-[3/5]" : "aspect-square"
      }`}
    >
      <div
        className="absolute inset-0 transition-transform duration-700 group-hover:scale-110"
        style={{ background: item.gradient }}
      />
      <div className="absolute inset-0 ring-1 ring-white/10 group-hover:ring-2 group-hover:ring-pink-500/40 rounded-2xl transition-all duration-500 group-hover:shadow-[0_0_60px_rgba(236,72,153,0.4)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
        <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 font-bold">
          {item.subtitle}
        </div>
        <div className="font-display font-extrabold text-white text-lg tracking-tight mt-0.5">
          {item.title}
        </div>
      </div>
    </div>
  );
};

// Modal for case-study detail playback (videos, photos, brand cases)
const DetailModal = ({ item, onClose }) => {
  if (!item) return null;
  const isVideo = item.type === "video";
  const isPhoto = item.type === "photo";
  const isBrand = item.type === "brand";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white z-10"
        aria-label="Schließen"
      >
        <Icons.X size={20} />
      </button>

      <div
        className="relative w-full max-w-5xl max-h-[88vh] rounded-3xl overflow-hidden bg-neutral-950 grid lg:grid-cols-12 gap-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Visual side */}
        <div className="lg:col-span-7 relative bg-neutral-900 flex items-center justify-center min-h-[280px] lg:min-h-[520px]">
          {isVideo && (
            <video
              src={item.src}
              autoPlay
              controls
              playsInline
              className="w-full h-full object-cover"
            />
          )}
          {isPhoto && (
            <img
              src={item.src}
              alt={item.title}
              className="w-full h-full object-cover"
            />
          )}
          {isBrand && (
            <div
              className={`w-full h-full flex items-center justify-center px-10 py-14 ${
                item.logo?.bg === "dark" ? "bg-neutral-900" : "bg-white"
              }`}
            >
              <img
                src={item.logo.src}
                alt={item.title}
                className="max-w-[80%] max-h-[80%] object-contain"
              />
            </div>
          )}
        </div>

        {/* Info side */}
        <div className="lg:col-span-5 p-7 md:p-10 text-white relative overflow-y-auto max-h-[88vh]">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.25),transparent_70%)] pointer-events-none" />

          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-pink-300">
              {item.subtitle}
            </div>
            <h3 className="mt-2 font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-tight">
              {item.title}
            </h3>

            {item.metric && (
              <div className="mt-4 inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-sm font-bold">
                {item.metric}
              </div>
            )}

            <div className="mt-6 space-y-4 text-neutral-300 text-sm leading-relaxed">
              <p>
                {isVideo &&
                  "Performance-orientiertes Eventvideo mit klarer Hook-Logik, dynamischem Schnitt und auf Aufmerksamkeit optimiertem Storytelling."}
                {isPhoto &&
                  "Foto-Produktion aus unserer Eventreihe – authentische Stimmung, professionelles Licht, hochwertige Bildsprache."}
                {isBrand &&
                  "Wir haben für diese Brand Content erstellt, der nicht nur sichtbar war, sondern Performance gebracht hat – mit klarer Strategie und durchdachter Umsetzung."}
              </p>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400">
                    Format
                  </div>
                  <div className="mt-1 font-bold">
                    {isVideo ? "Eventvideo / Reel" : isPhoto ? "Foto-Serie" : "Brand-Kampagne"}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400">
                    Status
                  </div>
                  <div className="mt-1 font-bold text-green-400">
                    Veröffentlicht
                  </div>
                </div>
              </div>
            </div>

            <a
              href="/kontakt"
              className="mt-8 inline-flex items-center gap-3 px-6 py-4 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-xs font-bold tracking-[0.12em] uppercase hover:shadow-[0_18px_42px_-10px_rgba(236,72,153,0.7)] transition-all"
            >
              Ähnliches Projekt anfragen
              <Icons.ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const ErfolgePage = () => {
  const [active, setActive] = useState("premium");
  const [modalItem, setModalItem] = useState(null);
  const [grouped, setGrouped] = useState(null);

  useEffect(() => {
    fetchErfolge()
      .then((d) => setGrouped(d.grouped || {}))
      .catch(() => setGrouped(null));
  }, []);

  // Normalize API items to the shape the cards expect (with nested logo)
  const items = useMemo(() => {
    let raw;
    if (grouped && Array.isArray(grouped[active]) && grouped[active].length > 0) {
      raw = grouped[active];
    } else {
      // Fallback to mock so the site never goes empty
      return ERFOLGE_ITEMS[active] || [];
    }
    return raw.map((it) => {
      const src = resolveAssetUrl(it.src);
      const base = { ...it, src };
      if (it.type === "brand") {
        return { ...base, logo: { src, bg: it.logo_bg || "dark" } };
      }
      if (it.type === "external") {
        return {
          ...base,
          brandLogo: it.src ? { src } : null,
        };
      }
      return base;
    });
  }, [grouped, active]);

  const isFotos = active === "fotos";

  return (
    <>
      <SEO
        title="Erfolge – Cases, Kampagnen & Reichweite"
        description="Eine Auswahl unserer Arbeiten – Premium-Brands (Aldi, Amazon, Subway, Five Guys), Stars, Clubs, Gastronomie, UGC und Sport. Über 200 Kampagnen ausgeliefert."
        path="/erfolge"
        keywords="Referenzen, Case Studies, Social Media Kampagnen, Aldi TikTok, Subway Reel, UGC Cases, Content Beispiele"
      />
      <PageHero
        eyebrow="Erfolge"
        title="Content,"
        gradient="der wirkt."
        subtitle="Eine Auswahl unserer Arbeiten – von Premium-Brands über Stars und Clubs bis hin zu Gastronomie, UGC und Sport."
      />

      {/* Tab navigation */}
      <section className="sticky top-24 z-30 bg-white/85 backdrop-blur-xl border-y border-black/5">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <div className="flex gap-2 md:gap-3 overflow-x-auto py-4 no-scrollbar">
            {ERFOLGE_CATEGORIES.map((cat) => {
              const Ico = Icons[cat.icon] || Icons.Sparkles;
              const isActive = active === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActive(cat.id)}
                  className={`shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? "bg-neutral-950 text-white shadow-[0_8px_24px_-8px_rgba(0,0,0,0.4)]"
                      : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  <Ico
                    size={15}
                    className={isActive ? "text-pink-400" : "text-neutral-500"}
                  />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Items grid */}
      {isFotos ? (
        <section className="py-20 md:py-28 bg-[#171717] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.08),rgba(168,85,247,0.05)_40%,transparent_70%)]" />
          </div>
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em] text-white">
                  Foto-Galerie
                </h2>
                <p className="mt-2 text-neutral-400 max-w-xl">
                  Eine Auswahl unserer Foto-Produktionen.
                </p>
              </div>
              <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">
                {items.length} Projekte
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {items.map((item, i) => (
                <PhotoCard key={item.id} item={item} span={i % 5 === 0} onOpen={setModalItem} />
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="py-20 md:py-28 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
              {items.map((item) => {
                if (item.type === "video") {
                  return <VideoCard key={item.id} item={item} onOpen={setModalItem} />;
                }
                if (item.type === "external") {
                  return (
                    <SocialEmbed
                      key={item.id}
                      url={item.url}
                      title={item.title}
                      subtitle={item.platform || item.subtitle}
                      metric={item.metric}
                      gradient={item.gradient}
                      brandLogo={item.brandLogo}
                      theme="light"
                    />
                  );
                }
                if (item.type === "brand") {
                  return <BrandCard key={item.id} item={item} onOpen={setModalItem} />;
                }
                return <ImageCard key={item.id} item={item} onOpen={setModalItem} />;
              })}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em] text-neutral-950">
            Du möchtest, dass deine Marke{" "}
            <span className="bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent">
              hier auch landet?
            </span>
          </h2>
          <Link
            to="/kontakt"
            className="mt-8 inline-flex items-center gap-3 px-8 py-5 rounded-full bg-neutral-950 text-white text-sm font-bold tracking-[0.12em] uppercase hover:bg-neutral-800 transition-all duration-300 group"
          >
            Projekt anfragen
            <Icons.ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>
      </section>

      <TestimonialsStrip max={2} eyebrow="Was Kunden nach der Arbeit sagen" variant="dark" />

      <DetailModal item={modalItem} onClose={() => setModalItem(null)} />
    </>
  );
};

export default ErfolgePage;
